// src/components/tables/SensorsTable.tsx
import React, { useEffect, useState } from 'react';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SensorItem } from '../../types/ProductItems';
import { getEditDeleteColumns } from '../../utils/columnsHelpers';
import ProductStatusDot from '../common/ProductStatusDot';

interface SensorsTableProps {
  data: SensorItem[];
  onEdit: (item: SensorItem) => void;
  onDelete: (key: string) => void;
}

const SensorsTable: React.FC<SensorsTableProps> = ({ data, onEdit, onDelete }) => {
  const columns: ColumnsType<SensorItem> = [
    {
      title: 'Бренд',
      dataIndex: 'brand',
      key: 'brand',
      sorter: (a, b) => (a.brand || '').localeCompare(b.brand || ''),
      render: (brand: string, record) => (
        <span style={{ display: 'flex', alignItems: 'center' }}>
          <ProductStatusDot item={record} />
          {brand}
        </span>
      ),
    },
    {
      title: 'Номер детали',
      dataIndex: 'partNumber',
      key: 'partNumber',
      sorter: (a, b) => (a.partNumber || '').localeCompare(b.partNumber || ''),
    },
    {
      title: 'SKU',
      dataIndex: 'sku',
      key: 'sku',
      sorter: (a, b) => (a.sku ?? '').localeCompare(b.sku ?? ''),
      width: 90,
      render: (val: string) => val || '—',
    },
    {
      title: 'Модель',
      dataIndex: 'model',
      key: 'model',
      sorter: (a, b) => (a.model || '').localeCompare(b.model || ''),
      render: (val: string) => val || '—',
    },
    {
      title: 'Тип датчика',
      dataIndex: 'sensorType',
      key: 'sensorType',
      sorter: (a, b) => (a.sensorType || '').localeCompare(b.sensorType || ''),
      render: (val: string) => val || '—',
    },
    {
      title: 'Тип подключения',
      dataIndex: 'connectionType',
      key: 'connectionType',
      sorter: (a, b) => (a.connectionType || '').localeCompare(b.connectionType || ''),
      render: (val: string) => {
        if (val === 'wired') return 'Проводной';
        if (val === 'wireless') return 'Беспроводной';
        // Если пришло другое значение из базы (например, "квадратный"), делаем первую букву заглавной
        return val ? val.charAt(0).toUpperCase() + val.slice(1) : '—';
      },
    },
    {
      title: 'Тип коннектора',
      dataIndex: 'connectorType',
      key: 'connectorType',
      sorter: (a, b) => (a.connectorType || '').localeCompare(b.connectorType || ''),
      render: (val: string) => (val ? val.charAt(0).toUpperCase() + val.slice(1) : '—'),
    },
    {
      title: 'Количество контактов',
      dataIndex: 'contactsCount', // <-- Исправлено с pinCount
      key: 'contactsCount',
      sorter: (a, b) => (Number(a.contactsCount) || 0) - (Number(b.contactsCount) || 0),
      render: (val: any) => val ?? '—',
    },
    {
      title: 'Количество',
      dataIndex: 'quantity',
      key: 'quantity',
      sorter: (a, b) => (Number(a.quantity) || 0) - (Number(b.quantity) || 0),
    },
    {
      title: 'Тип товара',
      dataIndex: 'original',
      key: 'original',
      filters: [
        { text: 'Оригинал', value: 'original' },
        { text: 'Копия', value: 'copy' },
      ],
      onFilter: (value, record) => record.original === value,
      render: (val: string) => (val === 'original' ? 'Оригинал' : val === 'copy' ? 'Копия' : '—'),
      sorter: (a, b) => (a.original || '').localeCompare(b.original || ''),
    },
    {
      title: 'Цена',
      dataIndex: 'price',
      key: 'price',
      sorter: (a, b) => (Number(a.price) || 0) - (Number(b.price) || 0),
      render: (price: number) => (price != null ? `${price.toLocaleString('ru-RU')} ֏` : '—'),
    },
    ...getEditDeleteColumns<SensorItem>(onEdit, onDelete),
  ];

  const [highlightId, setHighlightId] = useState<string | null>(() =>
    localStorage.getItem('highlightProductId')
  );

  const [pageSize, setPageSize] = useState<number>(() => {
    const saved = Number(localStorage.getItem('sensors_pageSize'));
    return Number.isFinite(saved) && saved > 0 ? saved : 10;
  });
  const [current, setCurrent] = useState<number>(1);

  // Когда данные загружаются — переходим на страницу с выделенным товаром
  useEffect(() => {
    if (!highlightId || !data.length) return;
    const idx = data.findIndex((item) => item.id === highlightId);
    if (idx !== -1) setCurrent(Math.floor(idx / pageSize) + 1);
  }, [highlightId, data.length, pageSize]);

  useEffect(() => {
    if ((current - 1) * pageSize >= data.length && data.length > 0) {
      setCurrent(1);
    }
  }, [data.length, pageSize, current]);

  return (
    <Table
      columns={columns}
      dataSource={data}
      pagination={{
        current,
        pageSize,
        total: data.length,
        position: ['bottomCenter'],
        showSizeChanger: true,
        pageSizeOptions: [10, 20, 50, 100],
        onChange: (page, size) => {
          setCurrent(page);
          if (size !== pageSize) {
            setPageSize(size);
            localStorage.setItem('sensors_pageSize', String(size));
          }
        },
        onShowSizeChange: (_page, size) => {
          setCurrent(1);
          setPageSize(size);
          localStorage.setItem('sensors_pageSize', String(size));
        },
      }}
      rowKey="id"
      size="middle"
      scroll={{ x: 1200 }}
      rowClassName={(record) => (record.id === highlightId ? 'highlight-row' : '')}
      onRow={(record) => ({
        onDoubleClick: () => {
          if (highlightId === record.id) {
            localStorage.removeItem('highlightProductId');
            setHighlightId(null);
          }
        },
      })}
    />
  );
};

export default SensorsTable;